import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { DefaultPathEnum } from '../../../../main/constants/default-path.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { setForgotPasswordCode, setForgotPasswordEmail } from '../auth.slice.ts';
import { AUTH_SLICE_NAME, AuthSliceState } from '../auth.slice.types.ts';

interface Props {
  email: string;
}

export const sendPasswordResetCodeThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(
  `${AUTH_SLICE_NAME}/sendPasswordResetCode`,
  async (props, thunkAPI) => {
    const { email } = props;

    try {
      await getAwsAuthUtil().forgotPassword(email);
      thunkAPI.dispatch(setForgotPasswordEmail(email));
      thunkAPI.dispatch(setForgotPasswordCode(''));

      getTrackerUtil().track({
        event: TrackingEventEnum.USER_FORGOT_PASSWORD_EMAIL_SENT,
      });

      getToasterUtil().success(getLocaleUtil().select('auth:reset_code_sent'));
      getUrlUtil().navigateToUrl(DefaultPathEnum.AUTH_FORGOT_PASSWORD_VERIFY);
    } catch (error: unknown) {
      const { name, message } = error as Error;

      switch (name) {
        case 'UserNotFoundException':
          getToasterUtil().error(getLocaleUtil().select('auth:user_not_found'));
          break;
        default:
          getToasterUtil().error(message ?? getLocaleUtil().select('auth:reset_code_send_failed'));
          break;
      }
      throw error;
    }
  }
);

export const sendPasswordResetCodeThunkExtraReducers = (builder: ActionReducerMapBuilder<AuthSliceState>): void => {
  builder.addCase(sendPasswordResetCodeThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(sendPasswordResetCodeThunk.fulfilled, (state) => {
    state.isLoading = false;
  });

  builder.addCase(sendPasswordResetCodeThunk.rejected, (state) => {
    state.isLoading = false;
  });
};
